/**
 * AI experiments panel — the "AI experiments" tab of the Settings page.
 *
 * Two parts, following the Root Cause Themes precedent: a form to launch a run
 * and a history table of previous runs. The form deliberately does NOT save
 * over the production AI config — that is what Settings does. Here the config is
 * evaluated first, and only promoted afterwards from the run details page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import DatePicker from '@cloudscape-design/components/date-picker';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import Multiselect from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { useNavigate } from 'react-router-dom';
import { listExperiments, startExperiment } from '../../api/experiments';
import { getAIConfig, getFilters } from '../../api/tickets';
import { ExperimentRunSummary } from '../../types/experiment';
import {
  CAP_DEFAULT,
  CAP_MAX,
  ExperimentStatusBadge,
  POLL_INTERVAL_MS,
  absoluteTime,
  buildExperimentFilters,
  capError,
  countsText,
  dateRangeError,
  groupsText,
  isActive,
  progressText,
  scopeText,
  toGroupOptions,
  toSeverityOptions,
} from './experimentShared';

type Option = { label: string; value: string };

export function ExperimentsPanel() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groups, setGroups] = useState<Option[]>([]);
  const [groupOptions, setGroupOptions] = useState<Option[]>([]);
  const [groupsStatus, setGroupsStatus] = useState<'loading' | 'finished' | 'error'>('loading');
  const [severities, setSeverities] = useState<Option[]>([]);
  const [severityOptions, setSeverityOptions] = useState<Option[]>([]);
  const [ticketCap, setTicketCap] = useState(String(CAP_DEFAULT));

  const [runs, setRuns] = useState<ExperimentRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  /** Seed the editor from the live production config, so an experiment starts
   * from what is actually running rather than from blank fields. */
  useEffect(() => {
    getAIConfig()
      .then((res) => {
        if (!mountedRef.current) return;
        setModel((m) => m || res.config.model || '');
        setSystemPrompt((p) => p || res.config.system_prompt || '');
        setUserPrompt((p) => p || res.config.user_prompt || res.config.prompt || '');
      })
      .catch((e: Error) => {
        if (!mountedRef.current) return;
        // Do not fail silently: without this the model field stays empty, the
        // submit button stays disabled, and clicking it appears to do nothing.
        setError(
          `Could not load the current AI config (${e.message}). Enter a model ID manually to run an experiment.`,
        );
      });
  }, []);

  /** Populate the resolver-group dropdown from configured filters. Mirrors the
   * themes report, which is where the group list already comes from. */
  useEffect(() => {
    getFilters()
      .then(({ filters }) => {
        if (!mountedRef.current) return;
        setGroupOptions(toGroupOptions(filters));
        setSeverityOptions(toSeverityOptions(filters));
        setGroupsStatus('finished');
      })
      .catch(() => {
        if (!mountedRef.current) return;
        // Surfaced in the dropdown itself rather than swallowed: an unexplained
        // empty list is the same trap as a silently disabled button.
        setSeverityOptions(toSeverityOptions(undefined));
        setGroupsStatus('error');
      });
  }, []);

  const loadRuns = useCallback(() => {
    // Cancel any scheduled poll so manual refreshes and polls cannot stack.
    if (pollRef.current) clearTimeout(pollRef.current);
    listExperiments()
      .then((res) => {
        if (!mountedRef.current) return;
        const next = res.runs || [];
        setRuns(next);
        setLoading(false);
        // Keep polling while ANY run is still moving — multiple runs can be in
        // flight at once, each with its own worker invocation.
        if (next.some((r) => isActive(r.status))) {
          pollRef.current = setTimeout(loadRuns, POLL_INTERVAL_MS);
        }
      })
      .catch((e: Error) => {
        if (!mountedRef.current) return;
        setLoading(false);
        setError(e.message);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadRuns();
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [loadRuns]);

  const capNumber = Number(ticketCap);
  const capValidationError = capError(ticketCap);
  const rangeError = dateRangeError(dateFrom, dateTo);
  const submitBlockedReason = !model.trim()
    ? 'Enter a model ID to run an experiment.'
    : capValidationError
      ? capValidationError
      : rangeError
        ? rangeError
        : '';
  const canSubmit = !submitBlockedReason && !submitting;

  const handleSubmit = async () => {
    setError(null);
    setStarted(null);
    setSubmitting(true);
    try {
      const res = await startExperiment({
        name: name.trim() || undefined,
        config: {
          model: model.trim(),
          systemPrompt: systemPrompt.trim() || undefined,
          userPrompt: userPrompt.trim() || undefined,
        },
        filters: buildExperimentFilters({
          dateFrom,
          dateTo,
          groups: groups.map((g) => g.value),
          severities: severities.map((s) => s.value),
        }),
        ticketCap: ticketCap === '' ? undefined : capNumber,
      });
      // Stay on this page rather than navigating into the new run. Runs execute
      // in parallel — each is its own worker invocation — so the list is the
      // useful vantage point: it shows every run's live status at once and lets
      // another be launched immediately without losing the form.
      setStarted(res.runId);
      loadRuns();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const activeCount = runs.filter((r) => isActive(r.status)).length;

  const COLUMNS = [
    {
      id: 'runId',
      header: 'Run ID',
      cell: (item: ExperimentRunSummary) => (
        <Link
          href={`/experiments/${item.runId}`}
          onFollow={(e) => {
            e.preventDefault();
            navigate(`/experiments/${item.runId}`);
          }}
        >
          {item.runId.slice(0, 8)}
        </Link>
      ),
    },
    { id: 'name', header: 'Name', cell: (item: ExperimentRunSummary) => item.name || '—' },
    { id: 'model', header: 'Model', cell: (item: ExperimentRunSummary) => item.model || '—' },
    {
      id: 'scope',
      header: 'Time window',
      cell: (item: ExperimentRunSummary) => scopeText(item.filters),
    },
    {
      id: 'groups',
      header: 'Groups',
      cell: (item: ExperimentRunSummary) => groupsText(item.filters),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: ExperimentRunSummary) =>
        isActive(item.status) ? (
          <SpaceBetween size="xs" direction="horizontal">
            <ExperimentStatusBadge status={item.status} />
            <Box variant="small">{progressText(item as never)}</Box>
          </SpaceBetween>
        ) : (
          <ExperimentStatusBadge status={item.status} />
        ),
    },
    {
      id: 'counts',
      header: 'Results',
      cell: (item: ExperimentRunSummary) => countsText(item.counts),
    },
    {
      id: 'createdAt',
      header: 'Started',
      cell: (item: ExperimentRunSummary) => (
        <span title={item.createdAt}>{absoluteTime(item.createdAt)}</span>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Box variant="p" color="text-body-secondary">
        Evaluate a candidate model or prompt against a scoped subset of tickets. Experiments never
        modify production summaries.
      </Box>
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {started && (
        <Alert
          type="success"
          dismissible
          onDismiss={() => setStarted(null)}
          header="Experiment started"
          action={
            <Button onClick={() => navigate(`/experiments/${started}`)}>View run</Button>
          }
        >
          Run {started.slice(0, 8)} is queued. Its live status appears in the table below — you can
          start another experiment straight away; runs execute in parallel.
        </Alert>
      )}

      <Container header={<Header variant="h2">Run an experiment</Header>}>
        <Form
          actions={
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              {/* Never leave a disabled primary button unexplained — a silent
                  no-op click is indistinguishable from a broken page. */}
              {submitBlockedReason && (
                <Box variant="small" color="text-status-error">
                  {submitBlockedReason}
                </Box>
              )}
              <Button
                variant="primary"
                formAction="none"
                disabled={!canSubmit}
                loading={submitting}
                onClick={handleSubmit}
              >
                Run experiment
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label="Name" description="Optional label to tell runs apart.">
                <Input value={name} onChange={(e) => setName(e.detail.value)} placeholder="nova-lite terser prompt" />
              </FormField>
              <FormField
                label="Model"
                description="Bedrock model ID to evaluate."
                errorText={!model.trim() ? 'Required' : ''}
              >
                <Input value={model} onChange={(e) => setModel(e.detail.value)} placeholder="us.amazon.nova-lite-v1:0" />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={3}>
              <FormField
                label="From"
                description="Leave both dates empty to cover all time."
                errorText={rangeError}
              >
                <DatePicker
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.detail.value)}
                  placeholder="YYYY/MM/DD"
                  invalid={!!rangeError}
                />
              </FormField>
              <FormField label="To">
                <DatePicker
                  value={dateTo}
                  onChange={(e) => setDateTo(e.detail.value)}
                  placeholder="YYYY/MM/DD"
                  invalid={!!rangeError}
                />
              </FormField>
              <FormField
                label="Ticket cap"
                description={`Bounds Bedrock spend. Max ${CAP_MAX}.`}
                errorText={capValidationError}
              >
                <Input
                  type="number"
                  value={ticketCap}
                  onChange={(e) => setTicketCap(e.detail.value)}
                />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={2}>
              <FormField
                label="Resolver groups"
                description="Select one or more. Empty means all groups."
              >
                <Multiselect
                  selectedOptions={groups}
                  options={groupOptions}
                  onChange={(e) => setGroups(e.detail.selectedOptions as Option[])}
                  placeholder="All resolver groups"
                  filteringType="auto"
                  statusType={groupsStatus}
                  loadingText="Loading resolver groups"
                  errorText="Could not load resolver groups. Retry, or leave empty for all groups."
                />
              </FormField>
              <FormField
                label="Severities"
                description="Only severities this app ingests are listed. Empty means all."
              >
                <Multiselect
                  selectedOptions={severities}
                  options={severityOptions}
                  onChange={(e) => setSeverities(e.detail.selectedOptions as Option[])}
                  placeholder="All severities"
                />
              </FormField>
            </ColumnLayout>

            <ExpandableSection headerText="Prompts">
              <SpaceBetween size="m">
                <FormField label="System prompt" stretch>
                  <Textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.detail.value)}
                    rows={4}
                  />
                </FormField>
                <FormField
                  label="User prompt template"
                  description="Supports {title}, {description} and {comment_summary}."
                  stretch
                >
                  <Textarea
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.detail.value)}
                    rows={6}
                  />
                </FormField>
              </SpaceBetween>
            </ExpandableSection>

            <Box variant="small" color="text-status-info">
              Results are written to a separate experiment store. Production summaries are not
              touched, and no ticket is re-summarized until you promote this config.
            </Box>
          </SpaceBetween>
        </Form>
      </Container>

      <Table
        columnDefinitions={COLUMNS}
        items={runs}
        loading={loading}
        loadingText="Loading experiment runs"
        variant="container"
        header={
          <Header
            variant="h2"
            counter={runs.length ? `(${runs.length})` : undefined}
            description={
              activeCount
                ? `${activeCount} running — status updates automatically every few seconds.`
                : 'Status updates automatically while a run is in progress.'
            }
            actions={<Button iconName="refresh" onClick={loadRuns} ariaLabel="Refresh runs" />}
          >
            Runs
          </Header>
        }
        empty={
          <Box textAlign="center" padding={{ vertical: 'm' }}>
            <b>No experiments yet</b>
            <Box variant="p" color="text-status-inactive">
              Run one above to compare a candidate config against production.
            </Box>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
